import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  createOutcomeReassessment,
  loadOutcomeReassessmentState,
  QualityApiError,
  qualityKeys,
} from '@/lib/api/quality.js';
import type {
  OutcomeReassessmentEligibility,
  OutcomeReassessmentReceipt,
  TestResult,
} from '@/lib/api/quality-types.js';

import { correctionRequestId } from './OutcomeCorrectionDialog.js';

import { isOutcomeReassessmentCandidate } from './quality-outcome.js';

export type DisplayReceipt = Pick<
  OutcomeReassessmentReceipt,
  'request_id' | 'status' | 'reason' | 'outcome_revision' | 'report_revision'
> &
  Partial<
    Pick<
      OutcomeReassessmentReceipt,
      | 'actor_id'
      | 'model'
      | 'rubric_version'
      | 'completed_at'
      | 'base_revision'
      | 'before'
      | 'after'
    >
  >;

const ACTIVE = new Set<OutcomeReassessmentReceipt['status']>([
  'queued',
  'running',
]);

export function reassessmentEligibilityReason(
  reason: string | null,
): string | null {
  if (!reason) return null;
  const messages: Record<string, string> = {
    non_execution:
      'Outcome reassessment is available only for executed test cases.',
    requires_terminal: 'Wait until this case reaches a terminal result.',
    manual_protected:
      'This case has a manual correction. Outcome reassessment cannot replace it.',
    deterministic_outcome:
      'This result was determined directly from execution evidence.',
    missing_identity:
      'This case is missing the durable identity required for reassessment.',
    missing_evidence:
      'This case does not have enough saved evidence for outcome reassessment.',
    active_request: 'An outcome reassessment is already in progress.',
    not_authorized:
      'You can review this reassessment history but cannot start a new reassessment.',
  };
  return Object.hasOwn(messages, reason)
    ? (messages[reason] ?? null)
    : reason.replace(/_/g, ' ');
}

/**
 * One plain sentence for a receipt, saying what the analysis decided and whether the case status
 * moved. A judge that re-affirms the outcome it was given also lands as `applied`, so the sentence
 * compares the before and after rather than assuming the case changed. Every terminal state except `applied` leaves the status alone, and the reviewer's first
 * question is always whether it changed — so each sentence answers that outright instead of leaving
 * them to infer it from a raw record.
 */
export function reassessmentReceiptMessage(
  receipt: DisplayReceipt | undefined,
): string | null {
  if (!receipt) return null;
  switch (receipt.status) {
    case 'queued':
      return 'Outcome reassessment queued.';
    case 'running':
      return 'Outcome reassessment is running.';
    case 'applied': {
      /** Three distinct cases, and the receipt cannot be made to answer for two of them. An older
       *  receipt records neither side, so neither "changed" nor "confirmed" can be claimed — only
       *  that a revision was written, which `applied` always means. */
      const before = auditOutcome(receipt.before);
      const after = auditOutcome(receipt.after);
      if (before === null || after === null)
        return 'The reassessment recorded a new assessment revision.';
      return before === after
        ? 'The reassessment confirmed the current status.'
        : 'The reassessment set a new status for this case.';
    }
    case 'unresolved':
      return 'The reassessment could not decide. The status is unchanged.';
    case 'failed':
      return receipt.reason === 'invalid_output'
        ? 'The model returned an unusable answer. The status is unchanged.'
        : receipt.reason === 'enqueue_error'
          ? 'The request never reached the reassessment queue. The status is unchanged.'
          : 'The reassessment failed. The status is unchanged.';
    case 'expired':
      return 'The reassessment ran out of time. The status is unchanged.';
    case 'superseded':
      return receipt.reason === 'evidence_changed'
        ? 'The case evidence changed while the reassessment ran, so its answer was discarded.'
        : 'The case changed while the reassessment ran, so its answer was discarded.';
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function auditOutcome(value: unknown): string | null {
  const outcome = record(value)?.outcome;
  return typeof outcome === 'string' ? outcome.replace(/_/g, ' ') : null;
}

function auditRationale(value: unknown): string | null {
  const rationale = record(record(value)?.classification)?.rationale;
  return typeof rationale === 'string' && rationale.trim()
    ? rationale.trim()
    : null;
}

export interface OutcomeReassessmentState {
  eligibility: OutcomeReassessmentEligibility | null;
  receipts: DisplayReceipt[];
  latest: DisplayReceipt | undefined;
  total: number;
  /** An analysis is queued or running right now, so a second request would be refused. */
  analyzing: boolean;
  /** The case can be analyzed and the viewer is allowed to start it. */
  canAnalyze: boolean;
  /** At least one past attempt exists, so its audit trail must stay reachable. */
  hasHistory: boolean;
  /** The eligibility probe failed outright, which is different from being ineligible. */
  unavailable: boolean;
  analyze: () => void;
  pending: boolean;
  errorMessage: string | null;
  loadMore: () => void;
  loadingMore: boolean;
  canLoadMore: boolean;
}

/**
 * Eligibility, history and the start-analysis mutation for one case. The row owns the affordances,
 * so this hook deliberately renders nothing: the analyze action and the audit trail are two separate
 * entries in the row menu, gated on `canAnalyze` and `hasHistory` respectively. An applied analysis
 * moves the case to an outcome that can no longer be reassessed, so those two gates must stay
 * independent or the audit trail of the very analysis that just ran becomes unreachable.
 */
export function useOutcomeReassessment({
  runId,
  result,
  enabled,
}: {
  runId: string;
  result: TestResult;
  enabled: boolean;
}): OutcomeReassessmentState {
  const queryClient = useQueryClient();
  const [historyLimit, setHistoryLimit] = useState(20);
  const baseQueryKey = qualityKeys.outcomeReassessment(runId, result.case_id);
  const queryKey = [
    ...baseQueryKey,
    result.outcome_revision,
    historyLimit,
  ] as const;
  const state = useQuery({
    queryKey,
    enabled,
    queryFn: () =>
      loadOutcomeReassessmentState(runId, result.case_id, historyLimit),
    placeholderData: (previous) => previous,
    refetchInterval: (query) => {
      const active = query.state.data?.active;
      return active && ACTIVE.has(active.status) ? 2_500 : false;
    },
  });
  const requestId = useRef<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => {
      const revision =
        state.data?.eligibility.current_revision ?? result.outcome_revision;
      if (revision == null)
        throw new Error('Reload the case revision before reassessment.');
      requestId.current ??= correctionRequestId();
      return createOutcomeReassessment(runId, {
        caseId: result.case_id,
        expectedRevision: revision,
        requestId: requestId.current,
      });
    },
    onSuccess: async () => {
      requestId.current = null;
      await queryClient.invalidateQueries({ queryKey: baseQueryKey });
    },
    onError: async (error) => {
      if (error instanceof QualityApiError) requestId.current = null;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: baseQueryKey }),
        queryClient.invalidateQueries({ queryKey: qualityKeys.run(runId) }),
        queryClient.invalidateQueries({ queryKey: qualityKeys.runsBase }),
        queryClient.invalidateQueries({
          queryKey: qualityKeys.reportBundleBase(runId),
        }),
      ]);
    },
  });
  const appliedRevision = state.data?.receipts.find(
    (receipt) => receipt.status === 'applied',
  )?.report_revision;
  useEffect(() => {
    if (appliedRevision == null) return;
    void queryClient.invalidateQueries({ queryKey: qualityKeys.run(runId) });
    void queryClient.invalidateQueries({ queryKey: qualityKeys.runsBase });
    void queryClient.invalidateQueries({
      queryKey: qualityKeys.reportBundleBase(runId),
    });
  }, [appliedRevision, queryClient, runId]);

  const data = state.data;
  const receipts: DisplayReceipt[] = data
    ? data.active
      ? [
          data.active,
          ...data.receipts.filter(
            (receipt) => receipt.request_id !== data.active?.request_id,
          ),
        ]
      : data.receipts
    : [];
  const latest = receipts[0];
  const analyzing = !!latest && ACTIVE.has(latest.status);
  const eligible = data?.eligibility.eligible ?? false;
  const candidate =
    isOutcomeReassessmentCandidate(result) || data?.active != null;
  return {
    eligibility: data?.eligibility ?? null,
    receipts,
    latest,
    total: data?.total ?? receipts.length,
    analyzing,
    canAnalyze: candidate && (eligible || analyzing),
    hasHistory: receipts.length > 0,
    unavailable: enabled && !state.isLoading && !data,
    analyze: () => mutation.mutate(),
    pending: mutation.isPending,
    errorMessage: mutation.isError
      ? mutation.error instanceof Error
        ? mutation.error.message
        : 'Outcome reassessment could not be started.'
      : null,
    loadMore: () => setHistoryLimit((limit) => limit + 20),
    loadingMore: state.isFetching,
    canLoadMore: receipts.length < (data?.total ?? 0),
  };
}

function ReceiptRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words">{value}</dd>
    </div>
  );
}

function ReceiptCard({ receipt }: { receipt: DisplayReceipt }): JSX.Element {
  const before = auditOutcome(receipt.before);
  const after = auditOutcome(receipt.after);
  const rationale = auditRationale(receipt.after);
  return (
    <section className="space-y-2 rounded-md border border-border p-3 text-xs">
      <p className="font-medium text-foreground">
        {reassessmentReceiptMessage(receipt)}
      </p>
      <dl className="space-y-1">
        <ReceiptRow
          label="Outcome"
          value={`${before ?? 'unknown'} → ${after ?? 'unknown'}`}
        />
        {rationale ? <ReceiptRow label="Rationale" value={rationale} /> : null}
        <ReceiptRow label="Model" value={receipt.model ?? 'unknown'} />
        <ReceiptRow
          label="Rubric"
          value={receipt.rubric_version ?? 'unknown'}
        />
        <ReceiptRow label="Started by" value={receipt.actor_id ?? 'unknown'} />
        <ReceiptRow
          label="Completed"
          value={receipt.completed_at ?? 'not finished'}
        />
        <ReceiptRow
          label="Revisions"
          value={`base ${receipt.base_revision ?? '—'} · outcome ${
            receipt.outcome_revision ?? '—'
          } · report ${receipt.report_revision ?? '—'}`}
        />
      </dl>
    </section>
  );
}

/**
 * The detail behind the row's AI analysis entries: what the latest attempt decided, and the full
 * audit trail of earlier attempts. The case status itself is the reviewer's primary feedback, so
 * nothing here is needed to understand that the analysis landed.
 */
export function OutcomeReassessmentDialog({
  open,
  onOpenChange,
  caseTitle,
  state,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseTitle: string;
  state: OutcomeReassessmentState;
}): JSX.Element {
  const ineligible =
    state.eligibility && !state.eligibility.eligible && !state.analyzing
      ? reassessmentEligibilityReason(state.eligibility.reason)
      : null;
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Outcome reassessment"
      description={caseTitle}
      className="max-w-2xl"
    >
      <div className="space-y-3">
        {state.unavailable ? (
          <p className="text-xs text-destructive">
            Outcome reassessment eligibility could not be loaded.
          </p>
        ) : null}
        {ineligible ? (
          <p className="text-xs text-muted-foreground">{ineligible}</p>
        ) : null}
        {state.errorMessage ? (
          <p className="text-xs text-destructive" role="alert">
            {state.errorMessage} Review the current case and try again.
          </p>
        ) : null}
        {state.receipts.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            This case has not been reassessed yet.
          </p>
        ) : (
          <div className="space-y-2">
            {state.receipts.map((receipt) => (
              <ReceiptCard key={receipt.request_id} receipt={receipt} />
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          {state.canAnalyze ? (
            <Button
              size="sm"
              disabled={state.pending || state.analyzing}
              onClick={state.analyze}
            >
              {state.analyzing
                ? 'Reassessing outcome…'
                : state.hasHistory
                  ? 'Reassess outcome with AI again'
                  : 'Reassess outcome with AI'}
            </Button>
          ) : null}
          {state.canLoadMore ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={state.loadingMore}
              onClick={state.loadMore}
            >
              {state.loadingMore
                ? 'Loading history…'
                : `Load more (${state.total - state.receipts.length})`}
            </Button>
          ) : null}
        </div>
      </div>
    </AppDialog>
  );
}
